import { QueryClient } from '@tanstack/react-query';
import { ServerEventValidationSchema } from '@thedealersconcierge/lib/cache/events';
import { transactionKeys } from './keys/transaction';
import { DealershipScope } from './scope';

const assertNever = (eventType: never): never => {
  throw new Error(`Unhandled server event type: ${JSON.stringify(eventType)}`);
};

/**
 * Translate a backend domain event into TanStack cache invalidations.
 *
 * Events are thin notifications (type + ids); we never read entity data from
 * them. Because keys are hierarchical, a coarse event such as
 * `transaction.updated` cascades to every sub-resource of that transaction.
 *
 * Invalidation only *refetches* queries that are currently active (have a
 * mounted observer — i.e. on screen): that is React Query's default
 * `refetchType: 'active'`, and we pass no override. So an event for a
 * transaction that is NOT in view never triggers a background fetch — it is a
 * no-op when the query is uncached, or just marks a cached-but-inactive query
 * stale so it refetches the next time it mounts.
 *
 * `scope` is supplied by the caller — the SSE connection already knows the
 * active dealership + viewer — so the event itself never carries the viewer.
 * The `switch` is exhaustive (`assertNever`): adding a variant to the shared
 * schema is a compile error until its invalidation is wired here, which keeps
 * the event taxonomy and the key taxonomy from drifting apart.
 */
export const applyServerEvent = (
  queryClient: QueryClient,
  scope: DealershipScope,
  event: ServerEventValidationSchema
): void => {
  switch (event.type) {
    case 'transaction.updated':
      void queryClient.invalidateQueries({
        queryKey: transactionKeys.detail(scope, event.transactionId)
      });
      return;
    default:
      return assertNever(event.type);
  }
};
